import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { promptLibraryService } from 'features/chat/agent-chat-module/services/prompt-library.service';
import { getUsers, type IamData } from 'features/iam/services/user-service';
import { HttpError } from 'lib/https';
import { useConfig } from 'hooks/use-config';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { PromptVisibility } from '../constants';
import type {
  CreateFolderInput,
  CreatePromptInput,
  PromptFolder,
  PromptItem,
  UpdateFolderInput,
  UpdatePromptInput,
} from '../types';

const PROMPTS_QUERY_KEY = 'prompt-library';

function usePromptContext() {
  const { configurationSettings } = useConfig();
  const user = useCommonStore((state) => state.user);
  const projectKey = configurationSettings?.blocksKey || '';
  const blocksKey = projectKey;
  const userId = user?.id || '';
  return {
    projectKey,
    blocksKey,
    userId,
    user,
    enabled: !!projectKey && !!blocksKey && !!userId,
  };
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof HttpError) {
    const payload = error.error as { detail?: string; message?: string; errors?: { message?: string } };
    if (typeof payload?.detail === 'string') return payload.detail;
    if (typeof payload?.message === 'string') return payload.message;
    if (typeof payload?.errors?.message === 'string') return payload.errors.message;
    if (error.status === 404) {
      return 'Prompt library API not found.';
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Request failed';
}

function assertSuccess(response: { success?: boolean; message?: string }) {
  if (!response?.success) {
    throw new Error(response?.message || 'Request failed');
  }
}

export function usePromptLibrary() {
  const { projectKey, blocksKey, userId, enabled } = usePromptContext();

  return useQuery({
    queryKey: [PROMPTS_QUERY_KEY, 'folders', projectKey, userId],
    queryFn: async () => {
      const response = await promptLibraryService.getFolders(projectKey, blocksKey, userId, 100, 0);
      assertSuccess(response);
      const folders = response.data?.folders ?? [];
      return {
        ...response.data,
        folders: folders.map((folder) => normalizePromptFolder(folder)),
      };
    },
    enabled,
  });
}

function normalizePromptFolder(
  folder: PromptFolder & { is_default?: boolean; owner_id?: string; owner_name?: string }
): PromptFolder {
  return {
    ...folder,
    isDefault: folder.isDefault ?? folder.is_default ?? false,
    ownerId: folder.ownerId ?? folder.owner_id ?? '',
    // Owner display names are resolved from IAM by ownerId; never assume the viewer.
    ownerName: folder.ownerName ?? folder.owner_name,
    visibility: folder.visibility ?? PromptVisibility.PUBLIC,
  };
}

function normalizePromptItem(prompt: PromptItem & { folder_id?: string; created_by?: string }): PromptItem {
  return {
    ...prompt,
    folderId: prompt.folderId ?? prompt.folder_id ?? '',
    createdBy: prompt.createdBy ?? prompt.created_by,
  };
}

/** Resolve owner user ids to IAM users (same pattern collaboration uses for participants). */
export function useOwnerDirectory(ownerIds: Array<string | undefined>) {
  const { configurationSettings } = useConfig();
  const ids = useMemo(
    () => Array.from(new Set(ownerIds.filter((id): id is string => !!id))).sort(),
    [ownerIds]
  );

  return useQuery({
    queryKey: [PROMPTS_QUERY_KEY, 'owners', ids],
    queryFn: async () => {
      if (!configurationSettings) return {};
      const response = await getUsers(
        { page: 0, pageSize: ids.length, filter: { userIds: ids } },
        configurationSettings
      );
      const directory: Record<string, IamData> = {};
      for (const iamUser of response.data ?? []) {
        if (iamUser?.itemId) directory[iamUser.itemId] = iamUser;
      }
      return directory;
    },
    enabled: !!configurationSettings && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePromptSearch(search: string) {
  const { projectKey, blocksKey, userId, enabled } = usePromptContext();
  const trimmedSearch = search.trim();

  return useQuery({
    queryKey: [PROMPTS_QUERY_KEY, 'search', projectKey, userId, trimmedSearch],
    queryFn: async () => {
      const response = await promptLibraryService.listPrompts(
        {
          owner_id: userId,
          project_key: projectKey,
          search: trimmedSearch,
          limit: 100,
          offset: 0,
        },
        blocksKey
      );
      assertSuccess(response);
      return (response.data?.prompts ?? []).map(normalizePromptItem);
    },
    enabled: enabled && trimmedSearch.length > 0,
  });
}

export function usePromptFolder(folderId: string, search = '') {
  const { projectKey, blocksKey, userId, enabled } = usePromptContext();

  return useQuery({
    queryKey: [PROMPTS_QUERY_KEY, 'folder', projectKey, folderId, search],
    queryFn: async () => {
      const [foldersResponse, promptsResponse] = await Promise.all([
        promptLibraryService.getFolders(projectKey, blocksKey, userId, 100, 0),
        promptLibraryService.listPrompts(
          {
            owner_id: userId,
            folder_id: folderId,
            project_key: projectKey,
            search: search.trim() || undefined,
            limit: 100,
            offset: 0,
          },
          blocksKey
        ),
      ]);
      assertSuccess(foldersResponse);
      assertSuccess(promptsResponse);

      const folderRaw = foldersResponse.data.folders.find((item) => item.id === folderId) || null;
      const folder = folderRaw ? normalizePromptFolder(folderRaw) : null;
      return {
        folder,
        prompts: (promptsResponse.data.prompts ?? []).map(normalizePromptItem),
      };
    },
    enabled: enabled && !!folderId,
  });
}

export function useCreatePromptFolder() {
  const queryClient = useQueryClient();
  const { projectKey, blocksKey, userId } = usePromptContext();

  return useMutation({
    mutationFn: async (input: CreateFolderInput) => {
      const response = await promptLibraryService.createFolder(
        {
          name: input.name,
          owner_id: userId,
          project_key: projectKey,
          visibility: input.visibility,
        },
        blocksKey
      );
      assertSuccess(response);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROMPTS_QUERY_KEY, 'folders', projectKey] });
    },
  });
}

export function useUpdatePromptFolder() {
  const queryClient = useQueryClient();
  const { projectKey, blocksKey } = usePromptContext();

  return useMutation({
    mutationFn: async (input: UpdateFolderInput) => {
      const response = await promptLibraryService.updateFolder(
        {
          folder_id: input.id,
          name: input.name,
          project_key: projectKey,
          visibility: input.visibility,
        },
        blocksKey
      );
      assertSuccess(response);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROMPTS_QUERY_KEY] });
    },
  });
}

export function useDeletePromptFolder() {
  const queryClient = useQueryClient();
  const { projectKey, blocksKey } = usePromptContext();

  return useMutation({
    mutationFn: async (folderId: string) => {
      const response = await promptLibraryService.deleteFolder(
        { folder_id: folderId, project_key: projectKey },
        blocksKey
      );
      assertSuccess(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROMPTS_QUERY_KEY] });
    },
  });
}

export function useCreatePrompt() {
  const queryClient = useQueryClient();
  const { projectKey, blocksKey, userId } = usePromptContext();

  return useMutation({
    mutationFn: async (input: CreatePromptInput) => {
      const response = await promptLibraryService.createPrompt(
        {
          folder_id: input.folderId,
          title: input.title,
          body: input.body,
          owner_id: userId,
          project_key: projectKey,
        },
        blocksKey
      );
      assertSuccess(response);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROMPTS_QUERY_KEY] });
    },
  });
}

export function useUpdatePrompt() {
  const queryClient = useQueryClient();
  const { projectKey, blocksKey } = usePromptContext();

  return useMutation({
    mutationFn: async (input: UpdatePromptInput) => {
      const response = await promptLibraryService.updatePrompt(
        {
          prompt_id: input.id,
          folder_id: input.folderId,
          title: input.title,
          body: input.body,
          project_key: projectKey,
        },
        blocksKey
      );
      assertSuccess(response);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROMPTS_QUERY_KEY] });
    },
  });
}

export function useDeletePrompt() {
  const queryClient = useQueryClient();
  const { projectKey, blocksKey } = usePromptContext();

  return useMutation({
    mutationFn: async (promptId: string) => {
      const response = await promptLibraryService.deletePrompt(
        { prompt_id: promptId, project_key: projectKey },
        blocksKey
      );
      assertSuccess(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROMPTS_QUERY_KEY] });
    },
  });
}

export type { PromptFolder, PromptItem };
